import { useUserRoleContext } from "../context/UserRoleContext";
import { ROLES } from "./roles";

/**
 * RoleGuard — renders children only if the user has one of the allowed roles
 * for the given module.
 *
 * @param {string}   moduleKey  - Key of the module in userRole.moduli (e.g. "razpisi", "projekti")
 * @param {string[]} roles      - Array of role strings that are allowed to see children
 * @param {React.ReactNode} fallback - Optional fallback UI when access is denied (default: null)
 * @param {string|null} vodilnaClanicaProjektaID - If provided, grants access to fakultetniSkrbnik
 *                                                  whose clanicaID matches this value
 */
const RoleGuard = ({
  moduleKey,
  roles = [],
  fallback = null,
  children,
  vodilnaClanicaProjektaID = null,
}) => {
  const { userRole, isLoadingUserRole } = useUserRoleContext();

  if (isLoadingUserRole) return null;
  if (!userRole) return fallback;

  const moduleRoles = userRole.moduli?.[moduleKey] ?? [];

  // If vodilnaClanicaProjektaID is provided, check if the user is a fakultetniSkrbnik of VODILNA CLANICA PROJEKTA
  if (vodilnaClanicaProjektaID) {
    const isSkrbnikOfClanica =
      moduleRoles.includes(ROLES.FAKULTETNI_SKRBNIK) &&
      userRole.clanicaID === vodilnaClanicaProjektaID;
    if (isSkrbnikOfClanica) return children;

    // Exclude skrbnik role when checking other roles, since skrbnik has special access
    const rolesWithoutSkrbnik = roles.filter(
      (r) => r !== ROLES.FAKULTETNI_SKRBNIK,
    );

    // Check if user has any of the other required roles (excluding skrbnik) only when VODILNA CLANICA PROJEKTA requirement is provided
    const hasAccess = moduleRoles.some((r) => rolesWithoutSkrbnik.includes(r));
    return hasAccess ? children : fallback;
  }

  // Regular role check for users if VODILNA CLANICA PROJEKTA requirement is not provided
  const hasAccess = moduleRoles.some((r) => roles.includes(r));
  return hasAccess ? children : fallback;
};

export default RoleGuard;
